import React, { useEffect, useMemo, useState } from 'react'
import { DashboardPresenter } from './dashboard-presenter'
import { RootState } from '../../store'
import { useSelector, useDispatch } from 'react-redux'
import {
  dashboardServices,
  investServices,
  kycServices,
  projectServices,
  withdrawalServices,
} from '../../services'
import {
  DashboardInvestmentsGraphs,
  DashboardSummary,
  DashboardInvestmentTransactions,
  InvestmentStatus_v1,
  DashboardInvestmentGraphValues,
  DashboardInvestmentTransactionsRaw,
  Investment,
  RawOpportunity,
  UserDetails,
  Withdrawal,
  OpportunityDetails,
  WithdrawalStatus,
  WithdrawalApprovalDetails,
} from '../../lib/types/propsType_v1'
import { useNavigate, useParams } from 'react-router-dom'
import { InvestmentSearchParams } from '../../services/types'
import { calculateStartDateFromFilter, getFormattedDate } from '../../lib/utils/helpers'
import { showBanner } from '../../global-state/banner-slice'
import { dashboardGraphFilter } from '../../lib/utils/message'
import { TransactionSearchParams } from '../../lib/types'

const DashboardSpecificInvestmentContainer = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { opportunityId } = params
  const { authToken, email, name, userId } = useSelector((state: RootState) => state.investor)
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>({
    totalInvestedAmount: 0,
    totalReturnsTillDate: 0,
    realizedGains: 0,
    unrealizedGains: 0,
    realizedGainsAmount: 0,
    unrealizedGainsAmount: 0,
    totalUnits: 0,
    currentNav: 0,
  })
  const [searchParams, setSearchParams] = useState<TransactionSearchParams>({
    limit: '10',
    offset: '0',
    investor_portfolio_id: 'All',
  })
  const [investmentsGraphData, setInvestmentsGraphData] = useState<DashboardInvestmentsGraphs>({
    investments: [[]],
  })
  const [transactionsData, setTransactionsData] = useState<DashboardInvestmentTransactions[]>([])
  const [investments, setInvestments] = useState<Array<Investment & RawOpportunity & UserDetails>>(
    [],
  )
  const [transactionsCurrentPage, setTransactionsCurrentPage] = useState(1)
  const [transactionsPagination, setTransactionsPagination] = useState({
    total: 10,
    limit: 5,
    count: 5,
    offset: 0,
  })
  const [investmentsGraphFilterValue, setInvestmentsGraphFilterValue] = useState<{
    text: string
    value: string
  }>(
    dashboardGraphFilter[2], // Default to 1M
  )
  const [activeWithdrawalRequest, setActiveWithdrawalRequest] = useState<Withdrawal>(
    {} as Withdrawal,
  )
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()
  const [maxWithdrawalAmount, setMaxWithdrawalAmount] = useState<string>('0')
  const [isLockInPeriodActive, setIsLockInPeriodActive] = useState<boolean>(false)
  const [endDateForLockIn, setEndDateForLockIn] = useState<string>('')

  const [investorPortfolioIds, setInvestorPortfolioIds] = useState<
    { value: string; text: string }[]
  >([{ text: 'All', value: 'All' }])
  const [userInvestmentData, setUserInvestmentData] = useState<Investment | null>(null)

  const handleFilterChange = (value: string) => {
    const selectedFilter = dashboardGraphFilter.find((filter) => filter.value === value)
    if (selectedFilter) {
      setInvestmentsGraphFilterValue(selectedFilter)
    }
  }

  // Fetch specific investment values
  const getTransactionsData = async () => {
    const response = await dashboardServices.getTransactionsByOpportunityId(
      String(opportunityId),
      {
        ...searchParams,
        investor_portfolio_id:
          searchParams.investor_portfolio_id === 'All' ? '' : searchParams.investor_portfolio_id,
      },
      {
        Authorization: authToken ? authToken : '',
      },
    )
    if (response?.status === 200) {
      const transactions =
        response?.transactions?.map((item: DashboardInvestmentTransactionsRaw) => {
          const date = new Date(item.date)
          const dateString = getFormattedDate(date, 'DD-MM-YYYY')
          return {
            transaction_date: dateString,
            transaction_type: item.txn_type,
            amount: item.amount,
            order_status: item.order_status,
            portfolio_id: item.portfolio_id,
          } as DashboardInvestmentTransactions
        }) ?? []
      setTransactionsData(transactions)
      setTransactionsPagination({
        ...transactionsPagination,
        total: response?.total,
        limit: response?.limit ?? transactionsPagination.limit,
        count: response?.count ?? transactionsPagination.count,
        offset: response?.offset ?? transactionsPagination.offset,
      })
    }
  }

  useEffect(() => {
    const getOpportunityById = async () => {
      const data = await projectServices.getOpportunityById(opportunityId as string, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        const opportunity = data.data
        setOpportunity(opportunity)
      }
    }

    const getUserInvestmentInOpportunity = async () => {
      return investServices.searchInvestments_v1(
        {
          investor_id: userId,
          limit: 1,
          offset: 0,
          opportunity_id: opportunityId,
        } as InvestmentSearchParams,
        {
          Authorization: authToken ? authToken : '',
        },
      )
    }

    /** Secondary-market buyers may hold via transaction_history only (no investments row). */
    const hasLedgerExposureForOpportunity = async (): Promise<boolean> => {
      const txnResponse = await dashboardServices.getTransactionsByOpportunityId(
        String(opportunityId),
        { limit: '1', offset: '0', investor_portfolio_id: 'All' },
        { Authorization: authToken ? authToken : '' },
      )
      if (txnResponse?.status !== 200) return false
      return Number(txnResponse?.total ?? 0) > 0 || (txnResponse?.transactions?.length ?? 0) > 0
    }

    const getMaxWithdrawalAmount = async () => {
      const data = await withdrawalServices.getMaxWithdrawalAmountByOpportunity(
        opportunityId as string,
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (data && data.data && data.status === 200) {
        setMaxWithdrawalAmount(String((data?.data?.maxAmount / 100).toFixed(2)))
        setIsLockInPeriodActive(data?.data?.lockIn?.isActive)
        setEndDateForLockIn(
          data?.data?.lockIn?.endDate ? String(data.data.lockIn.endDate).split('T')[0] : '',
        )
      }
    }
    // Fetch dashboard summary
    const getDashboardInvestmentSummary = async () => {
      const data = await dashboardServices.getInvestmentSummary(String(opportunityId), {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setDashboardSummary(data?.data)
      }
    }
    // Fetch dashboard investments values
    const getDashboardInvestmentsGraphs = async () => {
      const startDate = calculateStartDateFromFilter(investmentsGraphFilterValue.value)
      const data = await dashboardServices.getInvestmentsGraphsByOpportunityId(
        { startDate },
        String(opportunityId),
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (data && data.data && data.status === 200) {
        const response = data?.data
        const investments =
          response?.values?.map((item: DashboardInvestmentGraphValues) => ({
            date: item.date,
            currentInvestmentValue: item.currentInvestmentValue,
            opportunity_id: response?.opportunity_id,
            opportunity_title: response?.opportunity_title,
          })) ?? []
        setInvestmentsGraphData({ investments: [investments] })
      }
    }

    // Fetch in-progress investment details
    const getInProgressInvestmentsList = async () => {
      const response = await investServices.searchInvestments_v1(
        {
          investor_id: userId,
          limit: 20,
          offset: 0,
          opportunity_id: opportunityId,
        } as InvestmentSearchParams,
        {
          Authorization: authToken ? authToken : '',
        },
        `?status=${InvestmentStatus_v1.INITIATED}&status=${InvestmentStatus_v1.ORDER_PLACED}&status=${InvestmentStatus_v1.PAYMENT_DONE}`,
      )
      if (response?.status === 200) {
        setInvestments(response?.investments ?? [])
      }
    }

    // Fetch active withdrawal request
    const getUserActiveWithdrawalRequest = async () => {
      const response = await withdrawalServices.getActiveWithdrawalRequest(
        String(userId),
        String(opportunityId),
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (response?.data && response?.data?.withdrawal && response?.status === 200) {
        // Filter out settled withdrawal requests
        if (
          ![
            WithdrawalStatus.CANCELLED,
            WithdrawalStatus.REJECTED,
            WithdrawalStatus.TRADE_EXECUTED,
          ].includes(response?.data?.withdrawal?.status)
        ) {
          setActiveWithdrawalRequest(response?.data.withdrawal)
        }

        if (response?.data?.withdrawal?.status === WithdrawalStatus.ORDER_PLACED) {
          // Fetch approval details for this withdrawal
          try {
            const approvalResponse = await withdrawalServices.getWithdrawalApprovalDetails(
              response.data.withdrawal.id.toString(),
              {
                Authorization: authToken ? authToken : '',
              },
            )

            if (approvalResponse?.status === 200 && approvalResponse?.data) {
              const withdrawalWithApprovalDetails = {
                ...response.data.withdrawal,
                approvalDetails: {
                  withdrawalApprovalDate: approvalResponse.data.withdrawalApprovalDate,
                  amountReceivableDate: approvalResponse.data.amountReceivableDate,
                  grossMoneyToBeReceived: approvalResponse.data.grossMoneyToBeReceived
                    ? approvalResponse.data.grossMoneyToBeReceived / 100
                    : 0,
                  charges: {
                    exitLoad: approvalResponse.data.charges?.exitLoad
                      ? approvalResponse.data.charges.exitLoad / 100
                      : 0,
                    profitSharing: approvalResponse.data.charges?.profitSharing
                      ? approvalResponse.data.charges.profitSharing / 100
                      : 0,
                  },
                  netMoneyToBeReceived: approvalResponse.data.netMoneyToBeReceived
                    ? approvalResponse.data.netMoneyToBeReceived / 100
                    : 0,
                  withdrawalAmount: approvalResponse.data.withdrawalAmount
                    ? Number(approvalResponse.data.withdrawalAmount) / 100
                    : 0,
                  effectiveReturn: approvalResponse.data.effectiveReturn,
                },
              }
              setActiveWithdrawalRequest(withdrawalWithApprovalDetails)
            }
          } catch (error) {
            console.error('Error fetching approval details:', error)
          }
        }
      }
    }

    const getInvestorPortfolioIds = async () => {
      const response = await kycServices.getInvestorPortfolioIds(userId, {
        Authorization: authToken ? authToken : '',
      })
      if (response?.data && response?.status === 200) {
        const portfolioIds = response.data?.map((item: { investor_portfolio_id: string }) => ({
          value: item.investor_portfolio_id,
          text: item.investor_portfolio_id,
        }))
        setInvestorPortfolioIds([{ text: 'All', value: 'All' }, ...portfolioIds])
      }
    }

    if (authToken && userId) {
      getUserInvestmentInOpportunity().then(async (response) => {
        if (response?.status !== 200) return

        const hasInvestments =
          Number(response?.count ?? 0) !== 0 && Number(response?.total ?? 0) !== 0

        if (!hasInvestments) {
          const hasLedger = await hasLedgerExposureForOpportunity()
          if (!hasLedger) {
            dispatch(
              showBanner({
                text: 'You do not have any investments in that opportunity',
                variant: 'warning-banner',
              }),
            )

            navigate('/dashboard')
            return
          }
        } else if (response?.investments && response.investments.length > 0) {
          // Store the user's investment data for NAV calculation
          setUserInvestmentData(response.investments[0])
        }

        getOpportunityById()
        getMaxWithdrawalAmount()
        getDashboardInvestmentSummary()
        getDashboardInvestmentsGraphs()
        getTransactionsData()
        getInProgressInvestmentsList()
        getUserActiveWithdrawalRequest()
        getInvestorPortfolioIds()
      })
    }
  }, [])

  useEffect(() => {
    if (authToken) getTransactionsData()
  }, [transactionsCurrentPage, searchParams])

  useEffect(() => {
    if (authToken && userId && opportunityId) {
      const getDashboardInvestmentsGraphs = async () => {
        const startDate = calculateStartDateFromFilter(investmentsGraphFilterValue.value)
        const data = await dashboardServices.getInvestmentsGraphsByOpportunityId(
          { startDate },
          String(opportunityId),
          {
            Authorization: authToken ? authToken : '',
          },
        )
        if (data && data.data && data.status === 200) {
          const response = data?.data
          const investments =
            response?.values?.map((item: DashboardInvestmentGraphValues) => ({
              date: item.date,
              currentInvestmentValue: item.currentInvestmentValue,
              opportunity_id: response?.opportunity_id,
              opportunity_title: response?.opportunity_title,
            })) ?? []
          setInvestmentsGraphData({ investments: [investments] })
        }
      }

      getDashboardInvestmentsGraphs()
    }
  }, [investmentsGraphFilterValue.value, authToken, userId, opportunityId])

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = transactionsPagination
    setTransactionsCurrentPage(currentPage)

    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })

    setTransactionsPagination({ ...transactionsPagination, offset: (currentPage - 1) * limit })
  }

  const handleSearchParams = ({ name, value }: { name: string; value: string }) => {
    const { limit } = transactionsPagination

    setSearchParams({
      ...searchParams,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    })
    setTransactionsCurrentPage(1)
    setTransactionsPagination({
      ...transactionsPagination,
      offset: 0,
    })
  }

  const processedTransactionsData = useMemo(() => {
    return (
      transactionsData?.map((item: DashboardInvestmentTransactions) => ({
        ...item,
        asset_class: opportunity?.assetClass ?? '',
        opportunity_title: opportunity?.projectName ?? '',
      })) ?? []
    )
  }, [transactionsData, opportunity])

  return (
    <>
      <DashboardPresenter
        name={name}
        email={email}
        isDashboardInvestmentView
        opportunity={opportunity}
        dashboardSummary={dashboardSummary}
        investmentsData={investmentsGraphData}
        transactionsData={processedTransactionsData}
        inProgressInvestments={investments}
        activeWithdrawalRequests={activeWithdrawalRequest?.id ? [activeWithdrawalRequest] : []}
        currentPage={transactionsCurrentPage}
        pagination={transactionsPagination}
        maxWithdrawalAmount={maxWithdrawalAmount}
        isLockInPeriodActive={isLockInPeriodActive}
        endDateForLockIn={endDateForLockIn}
        portfolioIds={investorPortfolioIds}
        searchParams={searchParams}
        handleCurrentPage={handleCurrentPage}
        handleSearchParams={handleSearchParams}
        userInvestmentData={userInvestmentData}
        selectedFilter={investmentsGraphFilterValue}
        onFilterChange={handleFilterChange}
      />
    </>
  )
}

export { DashboardSpecificInvestmentContainer }
